import React from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Download, ChevronDown, AlertTriangle, TrendingUp, TrendingDown, Target, Info } from 'lucide-react';
// Mock Data
const radarData = [{
  competency: 'Strategic Thinking',
  self: 4.8,
  others: 4.2,
  fullMark: 5
}, {
  competency: 'Communication',
  self: 4.5,
  others: 3.1,
  fullMark: 5
}, {
  competency: 'Execution',
  self: 3.8,
  others: 4.5,
  fullMark: 5
}, {
  competency: 'Team Development',
  self: 4.0,
  others: 3.8,
  fullMark: 5
}, {
  competency: 'Emotional Intel',
  self: 4.2,
  others: 3.5,
  fullMark: 5
}, {
  competency: 'Innovation',
  self: 3.5,
  others: 3.9,
  fullMark: 5
}];
const weightedData = [{
  name: 'Strategic Thinking',
  raw: 4.2,
  weighted: 4.6
}, {
  name: 'Communication',
  raw: 3.1,
  weighted: 2.8
}, {
  name: 'Execution',
  raw: 4.5,
  weighted: 4.8
}, {
  name: 'Team Development',
  raw: 3.8,
  weighted: 3.5
}, {
  name: 'Emotional Intel',
  raw: 3.5,
  weighted: 3.2
}];
const heatmapData = [{
  name: 'Strategic Thinking',
  self: 4.8,
  manager: 4.5,
  peers: 4.1,
  direct: 4.0
}, {
  name: 'Communication',
  self: 4.5,
  manager: 3.5,
  peers: 3.0,
  direct: 2.8
}, {
  name: 'Execution',
  self: 3.8,
  manager: 4.8,
  peers: 4.5,
  direct: 4.4
}, {
  name: 'Team Development',
  self: 4.0,
  manager: 3.9,
  peers: 3.8,
  direct: 3.7
}, {
  name: 'Emotional Intel',
  self: 4.2,
  manager: 3.8,
  peers: 3.4,
  direct: 3.2
}];
const getHeatmapColor = (score: number) => {
  if (score >= 4.5) return 'bg-emerald-500 text-white';
  if (score >= 4.0) return 'bg-emerald-400 text-white';
  if (score >= 3.5) return 'bg-emerald-300 text-slate-800';
  if (score >= 3.0) return 'bg-amber-300 text-slate-800';
  if (score >= 2.5) return 'bg-amber-400 text-slate-900';
  return 'bg-rose-500 text-white';
};
export function ScoringDashboard() {
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-2318" data-mp="1uaqfm8qu2nyv">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4" data-id="element-2319" data-mp="1z91nidxkioeg">
        <div data-id="element-2320" data-mp="1l9jzi0s3jf65">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-2321" data-mp="1l90eucb2acof">
            Scoring Dashboard
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2322" data-mp="1qazilnaprblw">
            Detailed analytics and breakdown for Q3 Leadership Assessment.
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-2323" data-mp="1gayvrrdmydcy">
          <button className="border border-slate-200 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center bg-white" data-id="element-2324" data-mp="1gc0fslcmqa6t">
            <Download className="h-4 w-4 mr-2" data-id="element-2325" data-mp="3aogn7ph5vud9" />
            Export PDF
          </button>
          <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-2326" data-mp="z1eiucpcy7hj">
            Jane Doe (Self)
            <ChevronDown className="h-4 w-4 ml-2 opacity-70" data-id="element-2327" data-mp="18rpn60cyeioy" />
          </button>
        </div>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2328" data-mp="140hamvgbuh7k">
        {/* Overall Score */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-id="element-2329" data-mp="1hr2w8rx61595">
          <div className="flex items-center justify-between mb-2" data-id="element-2330" data-mp="1dq0x0b390t76">
            <h3 className="text-sm font-medium text-slate-500" data-id="element-2331" data-mp="1rqld2zhs4awz">
              Overall Score
            </h3>
            <Target className="h-4 w-4 text-indigo-500" data-id="element-2332" data-mp="1mr8uiqxxxq07" />
          </div>
          <div className="flex items-baseline space-x-2" data-id="element-2333" data-mp="1mpxlocpn1s28">
            <span className="text-3xl font-bold text-slate-900" data-id="element-2334" data-mp="3tizwy43vvpg1">4.1</span>
            <span className="text-sm text-slate-500" data-id="element-2335" data-mp="3ojx0v95vstk7">/ 5.0</span>
          </div>
          <div className="mt-2 text-xs text-emerald-600 flex items-center font-medium" data-id="element-2336" data-mp="3q0j47p6kwxxa">
            <TrendingUp className="h-3 w-3 mr-1" data-id="element-2337" data-mp="3uzdkokh2qae8" />
            +0.3 from last cycle
          </div>
        </div>

        {/* Highest Competencies */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-id="element-2338" data-mp="3udut97u9jo1">
          <div className="flex items-center justify-between mb-2" data-id="element-2339" data-mp="8r49pt7wq97v">
            <h3 className="text-sm font-medium text-slate-500" data-id="element-2340" data-mp="37a1frwss4ohq">
              Highest Competency
            </h3>
            <TrendingUp className="h-4 w-4 text-emerald-500" data-id="element-2341" data-mp="3c8cqsaut951j" />
          </div>
          <div className="text-lg font-semibold text-slate-900 truncate" data-id="element-2342" data-mp="3h7z56nmdkrbx">
            Execution
          </div>
          <div className="mt-1 flex items-center justify-between text-sm" data-id="element-2343" data-mp="3lyfje9y0sgzc">
            <span className="text-slate-500" data-id="element-2344" data-mp="3m0iidjh5hkg7">Score</span>
            <span className="font-medium text-emerald-600" data-id="element-2345" data-mp="2oiid5r2o3cg8">4.5 / 5.0</span>
          </div>
        </div>

        {/* Lowest Competencies */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-id="element-2346" data-mp="2tg1syvf0jxaa">
          <div className="flex items-center justify-between mb-2" data-id="element-2347" data-mp="1al5z1ohwyp8d">
            <h3 className="text-sm font-medium text-slate-500" data-id="element-2348" data-mp="16mq1iqo7hygp">
              Lowest Competency
            </h3>
            <TrendingDown className="h-4 w-4 text-rose-500" data-id="element-2349" data-mp="11l0pp8t5tgrk" />
          </div>
          <div className="text-lg font-semibold text-slate-900 truncate" data-id="element-2350" data-mp="wmfpvahtkz3n">
            Communication
          </div>
          <div className="mt-1 flex items-center justify-between text-sm" data-id="element-2351" data-mp="rp7eptv39vbw">
            <span className="text-slate-500" data-id="element-2352" data-mp="mxfmzg075jk6">Score</span>
            <span className="font-medium text-rose-600" data-id="element-2353" data-mp="mvuudvxg0aoy">3.1 / 5.0</span>
          </div>
        </div>

        {/* Blind Spots */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-id="element-2354" data-mp="dxj6i1nynr07">
          <div className="flex items-center justify-between mb-2" data-id="element-2355" data-mp="8xwq5jcu5un6">
            <h3 className="text-sm font-medium text-slate-500" data-id="element-2356" data-mp="8x500o20cp4a">Blind Spots</h3>
            <AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-2357" data-mp="404tkc18xkcn" />
          </div>
          <div className="flex items-baseline space-x-2" data-id="element-2358" data-mp="3qgc2gybhxv4l">
            <span className="text-3xl font-bold text-slate-900" data-id="element-2359" data-mp="3qiy98vqkc6lr">2</span>
            <span className="text-sm text-slate-500" data-id="element-2360" data-mp="3vgzv1ofdik0x">Identified</span>
          </div>
          <div className="mt-2 text-xs text-amber-600 flex items-center font-medium" data-id="element-2361" data-mp="3hh8izt8f3dpk">
            Self-rating significantly higher
          </div>
        </div>
      </div>

      {/* Middle Row: Radar & Competency Scores */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2362" data-mp="d3s9050nq46c">
        {/* Radar Chart (Self vs Others) */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-id="element-2363" data-mp="i2mqn3mw1mov">
          <div className="flex items-center justify-between mb-6" data-id="element-2364" data-mp="4958nn9uoi0y">
            <div data-id="element-2365" data-mp="89xj2jugksyt">
              <h3 className="text-lg font-semibold text-slate-900" data-id="element-2366" data-mp="89forsrcwotj">
                Self vs Others
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2367" data-mp="vygqyy9lj3qd">
                Radar comparison across all competencies
              </p>
            </div>
            <div className="p-2 bg-slate-50 rounded-md" data-id="element-2368" data-mp="2lgx44dou2sdb">
              <Info className="h-4 w-4 text-slate-400" data-id="element-2369" data-mp="2lfc8slup35ee" />
            </div>
          </div>
          <div className="h-[300px] w-full" data-id="element-2370" data-mp="2ggj67ogy4yt0">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2371" data-mp="2v81h57uy835i">
              <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData} data-id="element-2372" data-mp="2v9clp9w7xexo">
                <PolarGrid stroke="#e2e8f0" data-id="element-2373" data-mp="2q9712dod8sp3" />
                <PolarAngleAxis dataKey="competency" tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-2374" data-mp="22ikwwxhhfskd" />
                <PolarRadiusAxis angle={30} domain={[0, 5]} tick={{
                fill: '#94a3b8',
                fontSize: 10
              }} data-id="element-2375" data-mp="2chju9jwss1s2" />
                <Radar name="Self" dataKey="self" stroke="#818cf8" fill="#818cf8" fillOpacity={0.3} data-id="element-2376" data-mp="3num8w9fc6h9l" />
                <Radar name="Others (Avg)" dataKey="others" stroke="#34d399" fill="#34d399" fillOpacity={0.3} data-id="element-2377" data-mp="1gc06imu0zhnt" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                paddingTop: '20px'
              }} data-id="element-2378" data-mp="1q6ii0e5oohi6" />
                <Tooltip data-id="element-2379" data-mp="1zxv5ktkg8lie" />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Competency Scores (Bars) */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-id="element-2380" data-mp="28yl07txz45uj">
          <div className="flex items-center justify-between mb-6" data-id="element-2381" data-mp="2dy5wfwlre03k">
            <div data-id="element-2382" data-mp="2dxecjj1ixqt4">
              <h3 className="text-lg font-semibold text-slate-900" data-id="element-2383" data-mp="2dvtj5wqy4dwu">
                Competency Scores
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2384" data-mp="jjwqy1koj40q">
                Average scores by category
              </p>
            </div>
          </div>
          <div className="space-y-5" data-id="element-2385" data-mp="c26t5lmvws74">
            {radarData.map((item, idx) => <div key={idx} data-id="element-2386" data-mp="795cb834aikw" data-mpi={idx}>
                <div className="flex justify-between text-sm mb-1.5" data-id="element-2387" data-mp="77u8x9dl3cub">
                  <span className="font-medium text-slate-700" data-id="element-2388" data-mp="2bm1ki5monxp">
                    {item.competency}
                  </span>
                  <span className="text-slate-900 font-semibold" data-id="element-2389" data-mp="3tf0ww3sjtvq1">
                    {item.others.toFixed(1)}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden flex" data-id="element-2390" data-mp="q1gborykbye9">
                  <div className={`h-full rounded-full ${item.others >= 4.0 ? 'bg-emerald-500' : item.others >= 3.5 ? 'bg-indigo-400' : 'bg-amber-400'}`} style={{
                width: `${item.others / 5 * 100}%`
              }} data-id="element-2391" data-mp="l0iuahldn8dt"></div>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* Bottom Row: Weighted Scores & Heat Map */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2392" data-mp="2axwtv41xtzvk">
        {/* Weighted Scores */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-id="element-2393" data-mp="25zbu15qlli7n">
          <div className="flex items-center justify-between mb-6" data-id="element-2394" data-mp="2krdt4nkug76m">
            <div data-id="element-2395" data-mp="2fyb48sr0hlwr">
              <h3 className="text-lg font-semibold text-slate-900" data-id="element-2396" data-mp="2fxrptm0klfnh">
                Weighted Scores
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2397" data-mp="2tsu0emgd3t6m">
                Raw vs Role-Weighted Importance
              </p>
            </div>
          </div>
          <div className="h-[300px] w-full" data-id="element-2398" data-mp="33p6qzbgm1qx5">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2399" data-mp="33muc5765fwob">
              <BarChart data={weightedData} margin={{
              top: 20,
              right: 30,
              left: -20,
              bottom: 5
            }} layout="vertical" data-id="element-2400" data-mp="2yqvh50qbfgex">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" data-id="element-2401" data-mp="3fymoz4jmpa4g" />
                <XAxis type="number" domain={[0, 5]} tick={{
                fill: '#94a3b8',
                fontSize: 12
              }} data-id="element-2402" data-mp="3l0c1komi1vun" />
                <YAxis dataKey="name" type="category" tick={{
                fill: '#64748b',
                fontSize: 12
              }} width={120} data-id="element-2403" data-mp="2x8ol70mv636m" />
                <Tooltip cursor={{
                fill: '#f8fafc'
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
              }} data-id="element-2404" data-mp="2nc20cbujfc4l" />
                <Legend wrapperStyle={{
                fontSize: '12px'
              }} data-id="element-2405" data-mp="p11usdplqf59" />
                <Bar dataKey="raw" name="Raw Score" fill="#cbd5e1" radius={[0, 4, 4, 0]} barSize={12} data-id="element-2406" data-mp="k0xasdphj2aa" />
                <Bar dataKey="weighted" name="Weighted Score" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={12} data-id="element-2407" data-mp="b2lnakjx0km2" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Heat Map */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col" data-id="element-2408" data-mp="xzn89ug7v3z6">
          <div className="flex items-center justify-between mb-6" data-id="element-2409" data-mp="ty20uomgbhwd">
            <div data-id="element-2410" data-mp="1qiu86hpkgra2">
              <h3 className="text-lg font-semibold text-slate-900" data-id="element-2411" data-mp="1qjbzczxdgfb4">
                Rater Group Heat Map
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2412" data-mp="1lphie495iefq">
                Score distribution across rater types
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-x-auto" data-id="element-2413" data-mp="39dye7eayt0m4">
            <div className="min-w-[400px]" data-id="element-2414" data-mp="3deqqyhj1pnn5">
              {/* Header Row */}
              <div className="grid grid-cols-5 gap-2 mb-2" data-id="element-2415" data-mp="3dguyd9g5l4s6">
                <div className="col-span-1" data-id="element-2416" data-mp="3iems890cz0z0"></div>
                <div className="text-xs font-semibold text-slate-500 text-center uppercase tracking-wider" data-id="element-2417" data-mp="3ncy2gkukfdhr">
                  Self
                </div>
                <div className="text-xs font-semibold text-slate-500 text-center uppercase tracking-wider" data-id="element-2418" data-mp="3saq446l0idz5">
                  Manager
                </div>
                <div className="text-xs font-semibold text-slate-500 text-center uppercase tracking-wider" data-id="element-2419" data-mp="4yp64z6nqf6i">
                  Peers
                </div>
                <div className="text-xs font-semibold text-slate-500 text-center uppercase tracking-wider" data-id="element-2420" data-mp="9zl99aj6pxav">
                  Directs
                </div>
              </div>

              {/* Data Rows */}
              <div className="space-y-2" data-id="element-2421" data-mp="18l3g4n9fumy">
                {heatmapData.map((row, idx) => <div key={idx} className="grid grid-cols-5 gap-2 items-center" data-id="element-2422" data-mp="3sgfc72zv1y4g" data-mpi={idx}>
                    <div className="text-xs font-medium text-slate-700 truncate pr-2" title={row.name} data-id="element-2423" data-mp="61e7eqawmyxz">
                      {row.name}
                    </div>
                    <div className={`h-10 rounded-md flex items-center justify-center text-sm font-medium transition-colors ${getHeatmapColor(row.self)}`} data-id="element-2424" data-mp="39iwe2uexxzwt">
                      {row.self.toFixed(1)}
                    </div>
                    <div className={`h-10 rounded-md flex items-center justify-center text-sm font-medium transition-colors ${getHeatmapColor(row.manager)}`} data-id="element-2425" data-mp="13oml3z4nz3pf">
                      {row.manager.toFixed(1)}
                    </div>
                    <div className={`h-10 rounded-md flex items-center justify-center text-sm font-medium transition-colors ${getHeatmapColor(row.peers)}`} data-id="element-2426" data-mp="16bdrawxhnc49">
                      {row.peers.toFixed(1)}
                    </div>
                    <div className={`h-10 rounded-md flex items-center justify-center text-sm font-medium transition-colors ${getHeatmapColor(row.direct)}`} data-id="element-2427" data-mp="11aq4kngalhfu">
                      {row.direct.toFixed(1)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-6 flex items-center justify-center space-x-4 text-xs text-slate-500" data-id="element-2428" data-mp="1k7rbidteprmf">
            <div className="flex items-center" data-id="element-2429" data-mp="27xv47q5246yn">
              <div className="w-3 h-3 rounded-sm bg-rose-500 mr-1.5" data-id="element-2430" data-mp="2cpd5kfazs9k5"></div> &lt;
              3.0
            </div>
            <div className="flex items-center" data-id="element-2431" data-mp="2mlvg2g6tl07r">
              <div className="w-3 h-3 rounded-sm bg-amber-400 mr-1.5" data-id="element-2432" data-mp="2mkkcohhaduh6"></div> 3.0
              - 3.4
            </div>
            <div className="flex items-center" data-id="element-2433" data-mp="2ihnrqk49xma2">
              <div className="w-3 h-3 rounded-sm bg-emerald-300 mr-1.5" data-id="element-2434" data-mp="2djvurborv9z0"></div>{' '}
              3.5 - 3.9
            </div>
            <div className="flex items-center" data-id="element-2435" data-mp="28l2ifml81p0f">
              <div className="w-3 h-3 rounded-sm bg-emerald-500 mr-1.5" data-id="element-2436" data-mp="23m94xu5xpy09"></div>{' '}
              &gt; 4.0
            </div>
          </div>
        </div>
      </div>
    </div>;
}